import React from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { AnswerChoice } from 'modules/questionnaires/components/Answer/AnswerChoice'
import { AnswerNumber } from 'modules/questionnaires/components/Answer/AnswerNumber'
import { AnswerRating } from 'modules/questionnaires/components/Answer/AnswerRating'
import { AnswerText } from 'modules/questionnaires/components/Answer/AnswerText'
import { MemoizedSectionHeaderAddon } from 'modules/questionnaires/components/MemoizedSectionHeaderAddon'
import {
  isChoiceAnswer,
  isNumberAnswer,
  isQuestionChoice,
  isQuestionRating,
  isRatingAnswer,
  isSectionResponse,
  isTextAnswer,
} from 'modules/questionnaires/lib/helpers'
import {
  AnswerType,
  QuestionResponse,
  QuestionnaireResponseContent,
} from 'modules/questionnaires/lib/types'

type Props = {
  content: QuestionnaireResponseContent
  readonly?: boolean
  emptyState?: React.ReactElement
  onAnswer: (answer: AnswerType) => void
  renderSectionHeaderAddon?: (sectionId: string) => React.ReactNode | undefined
  hasAttemptedSubmit?: boolean
}

export const QuestionnaireResponse: React.FC<Props> = ({
  content,
  emptyState,
  readonly = false,
  onAnswer,
  renderSectionHeaderAddon,
  hasAttemptedSubmit = false,
}) => {
  if (!content.length) {
    return emptyState || <EmptyState />
  }

  const answer = (position: number, questionResponse: QuestionResponse) => {
    if (questionResponse.question.answerType === 'text' && isTextAnswer(questionResponse.answer)) {
      return (
        <AnswerText
          key={questionResponse.question.uuid}
          question={questionResponse.question}
          answer={questionResponse.answer}
          onChange={onAnswer}
          readonly={readonly}
          position={position}
          placeholder={i18n.t('questionnaires.answers.text.placeholder')}
          hasAttemptedSubmit={hasAttemptedSubmit}
        />
      )
    }
    if (isQuestionRating(questionResponse.question) && isRatingAnswer(questionResponse.answer)) {
      return (
        <AnswerRating
          key={questionResponse.question.uuid}
          question={questionResponse.question}
          answer={questionResponse.answer}
          onChange={onAnswer}
          readonly={readonly}
          position={position}
          hasAttemptedSubmit={hasAttemptedSubmit}
        />
      )
    }

    if (isQuestionChoice(questionResponse.question) && isChoiceAnswer(questionResponse.answer)) {
      return (
        <AnswerChoice
          key={questionResponse.question.uuid}
          question={questionResponse.question}
          answer={questionResponse.answer}
          onChange={onAnswer}
          readonly={readonly}
          position={position}
          hasAttemptedSubmit={hasAttemptedSubmit}
        />
      )
    }

    if (
      questionResponse.question.answerType === 'number' &&
      isNumberAnswer(questionResponse.answer)
    ) {
      return (
        <AnswerNumber
          key={questionResponse.question.uuid}
          question={questionResponse.question}
          answer={questionResponse.answer}
          onChange={onAnswer}
          readonly={readonly}
          position={position}
          placeholder={i18n.t('questionnaires.answers.text.placeholder')}
          hasAttemptedSubmit={hasAttemptedSubmit}
        />
      )
    }
  }

  return (
    <Box gap="s32" width="full">
      {content.map((item, i) => {
        const isSectionItem = isSectionResponse(item)
        const key = isSectionItem ? item.uuid : item.question.uuid
        return (
          <Box
            key={key}
            background="grey100"
            padding="s24"
            gap="s24"
            borderRadius={{ all: 'abs016' }}
            role={isSectionItem ? 'region' : null}
            aria-labelledby={isSectionItem ? `section-title-${item.uuid}` : undefined}
          >
            {isSectionItem && (
              <Box
                flexDirection="row"
                alignItems="center"
                gap="s8"
                justifyContent="spaceBetween"
                style={{ flexGrow: '1' }}
              >
                <Text weight="semibold" size="400" color="black" id={`section-title-${item.uuid}`}>
                  {item.title}
                </Text>
                {renderSectionHeaderAddon && (
                  <MemoizedSectionHeaderAddon
                    render={renderSectionHeaderAddon}
                    sectionId={item.uuid}
                  />
                )}
              </Box>
            )}

            {isSectionItem && (
              <>
                {item.questions.map((questionResponse, j) => {
                  return answer(j + 1, questionResponse)
                })}
              </>
            )}

            {!isSectionItem && answer(i + 1, item)}
          </Box>
        )
      })}
    </Box>
  )
}

const EmptyState: React.FC = () => {
  return (
    <Box padding="s24">
      <Box paddingX="s32" paddingY="s24" flexDirection="column" alignItems="center" gap="s12">
        <Icon.Large icon="page-edit" color="grey700" />
        <Box gap="s8" alignItems="center">
          <Text weight="semibold" size="400" color="grey1000">
            {i18n.t('questionnaires.questionnaire_response.empty_state.title')}
          </Text>
          <Text.Normal size="300" weight="normal" color="grey800">
            {i18n.t('questionnaires.questionnaire_response.empty_state.description')}
          </Text.Normal>
        </Box>
      </Box>
    </Box>
  )
}
